// This file must be in shared-ui instead of rst-ui so that Upsell::Tooltip can access the configs
import Enum from '@square/dashboard-utilities/utils/enum';
import { LANGUAGE_LOCALES as LOCALES } from '@dashboard/shared-ui/utils/rst-locales';
export const LOCALE = new Enum(['GB', 'US', 'AU', 'CA', 'IE', 'FR', 'ES', 'ca_ES', 'JP', 'es_CA', 'fr_CA', 'es_US']);
export const UPSELL_PROMO_TYPE = new Enum(['AUTO_GRATUITY', 'COURSE_MANAGEMENT', 'MENU_REPORTS', 'NOTIFY_WHEN_READY', 'REOPEN_CLOSED_CHECKS', 'SEAT_MANAGEMENT', 'SECTION_SALES', 'SHARED_SETTINGS', 'TABLE_TIMERS', 'SCAN_TO_PAY', 'COMBOS', 'KIOSK']);
export function localeForConfig(merchantCountryCode, browserLanguage = null) {
  const configuredLocaleCodes = new Set([LOCALES.AU, LOCALES.CA, LOCALES.ES, LOCALES.FR, LOCALES.GB, LOCALES.IE, LOCALES.JP, LOCALES.US, LOCALES.ca_ES, LOCALES.es_CA, LOCALES.es_US, LOCALES.fr_CA]);
  if (configuredLocaleCodes.has(merchantCountryCode)) {
    const twoLetterLangCode = browserLanguage && browserLanguage.slice(0, 2); // e.g. change 'fr-CA' to 'fr'
    const maybeLangCountryLocale = `${twoLetterLangCode}_${merchantCountryCode}`; // e.g. 'fr_CA'

    return configuredLocaleCodes.has(maybeLangCountryLocale) ? maybeLangCountryLocale : merchantCountryCode;
  }
  return LOCALES.US;
}

/**
 * Per RST-20002, a few assets specifically have been localized for fr-CA for compliance
 * with Quebec law. This method wraps localeForConfig to be used by components that require
 * a change to remain in compliance.
 */
export function localeForConfigFrenchCanadaOverride(merchantCountryCode, browserLanguage) {
  const selectedLocale = localeForConfig(merchantCountryCode, browserLanguage);
  if (selectedLocale === 'CA' && browserLanguage === 'fr-CA') {
    return 'fr_CA';
  }
  return selectedLocale;
}

// US images are the default
export const DefaultUpsellTooltipImageConfigs = {
  [UPSELL_PROMO_TYPE.AUTO_GRATUITY]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.COURSE_MANAGEMENT]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/notify-when-ready_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/notify-when-ready_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.SEAT_MANAGEMENT]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.SHARED_SETTINGS]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/shared-settings_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/shared-settings_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.TABLE_TIMERS]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.SCAN_TO_PAY]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tooltip/scan-to-pay_US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tooltip/scan-to-pay_US@2x.png'
    }
  },
  [UPSELL_PROMO_TYPE.KIOSK]: {
    src: {
      '1x': '/@dashboard/rst-ui/images/upsell/tiered-subscription/kiosk-screen--en-US@1x.png',
      '2x': '/@dashboard/rst-ui/images/upsell/tiered-subscription/kiosk-screen--en-US@2x.png'
    }
  }
};

// For each feature, a mapping from locale to image config
export const UpsellTooltipImageConfigs = {
  [UPSELL_PROMO_TYPE.AUTO_GRATUITY]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.autoGratuity.description',
    [LOCALES.AU]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_AU@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_AU@2x.png'
      }
    },
    // cailin - France and Spain are missing because we do not show auto gratuity there.
    [LOCALES.GB]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_GB@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_GB@2x.png'
      }
    },
    [LOCALES.IE]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_IE@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/auto-gratuity_IE@2x.png'
      }
    },
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.AUTO_GRATUITY]
  },
  [UPSELL_PROMO_TYPE.COURSE_MANAGEMENT]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.courseManagement.description',
    [LOCALES.AU]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.COURSE_MANAGEMENT],
    [LOCALES.ca_ES]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_ca_ES@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_ca_ES@2x.png'
      }
    },
    [LOCALES.ES]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_ES@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_ES@2x.png'
      }
    },
    [LOCALES.FR]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_FR@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_FR@2x.png'
      }
    },
    [LOCALES.GB]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_GB@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_GB@2x.png'
      }
    },
    [LOCALES.IE]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_IE@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_IE@2x.png'
      }
    },
    [LOCALES.JP]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_JP@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/course-management_JP@2x.png'
      }
    },
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.COURSE_MANAGEMENT]
  },
  [UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.notifyWhenReady.description',
    [LOCALES.AU]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.ca_ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.FR]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.GB]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.IE]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.JP]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY],
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.NOTIFY_WHEN_READY]
  },
  [UPSELL_PROMO_TYPE.SCAN_TO_PAY]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.scanToPay.description',
    [LOCALES.AU]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.ca_ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.FR]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.GB]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.IE]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.JP]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY],
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SCAN_TO_PAY]
  },
  [UPSELL_PROMO_TYPE.SEAT_MANAGEMENT]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.seatManagement.description',
    [LOCALES.AU]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SEAT_MANAGEMENT],
    [LOCALES.ca_ES]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_ca_ES@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_ca_ES@2x.png'
      }
    },
    [LOCALES.ES]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_ES@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_ES@2x.png'
      }
    },
    [LOCALES.FR]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_FR@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_FR@2x.png'
      }
    },
    [LOCALES.GB]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_GB@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_GB@2x.png'
      }
    },
    [LOCALES.IE]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_IE@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_IE@2x.png'
      }
    },
    [LOCALES.JP]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_JP@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/seat-management_JP@2x.png'
      }
    },
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SEAT_MANAGEMENT]
  },
  [UPSELL_PROMO_TYPE.SHARED_SETTINGS]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.sharedSettings.description',
    [LOCALES.AU]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.ca_ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.FR]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.GB]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.IE]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.JP]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS],
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.SHARED_SETTINGS]
  },
  [UPSELL_PROMO_TYPE.TABLE_TIMERS]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.tableTimers.description',
    [LOCALES.AU]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_AU-GB@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_AU-GB@2x.png'
      }
    },
    [LOCALES.ca_ES]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_ca_ES@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_ca_ES@2x.png'
      }
    },
    [LOCALES.ES]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_ES@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_ES@2x.png'
      }
    },
    [LOCALES.FR]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_FR@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_FR@2x.png'
      }
    },
    [LOCALES.GB]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_AU-GB@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_AU-GB@2x.png'
      }
    },
    [LOCALES.IE]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_AU-IE@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_AU-IE@2x.png'
      }
    },
    [LOCALES.JP]: {
      src: {
        '1x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_JP@1x.png',
        '2x': '/@dashboard/rst-ui/images/upsell/tooltip/table-timers_JP@2x.png'
      }
    },
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.TABLE_TIMERS]
  },
  [UPSELL_PROMO_TYPE.KIOSK]: {
    key: 'sharedUi.images.restaurants.upsell.tooltip.kiosk.description',
    [LOCALES.AU]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.ca_ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.ES]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.FR]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.GB]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.IE]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.JP]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK],
    [LOCALES.US]: DefaultUpsellTooltipImageConfigs[UPSELL_PROMO_TYPE.KIOSK]
  }
};